/**
 * Home "Your stats" strip.
 *
 * Renders the KPIs the user pinned in the operations-manager Stats Catalog as a
 * single ordered grid (pin order = user order) with live sparkline values
 * (polled while the tab is visible). Supports drag-to-reorder, a compact/
 * comfortable density toggle (persisted), and a per-KPI detail popover with the
 * threshold legend. Capability-gated (fail-closed): the whole surface — the live
 * strip AND the "Browse catalog" discovery CTA — renders only for a subject that
 * holds a metrics-view capability the catalog requires; otherwise it renders
 * nothing. Shows a slim discovery empty-state when nothing is pinned yet, so an
 * authorized user can still find the feature.
 *
 * Cross-view live update: the catalog dispatches `empowernow:kpi-pins-changed`
 * on save; this strip listens and re-resolves without a full remount.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type KpiCatalog,
  type KpiMeta,
  type KpiSeries,
  filterByCapability,
  reorderByIds,
} from './kpiFormat';
import { fetchCatalog, fetchPins, fetchSnapshot, savePins } from './monitoringSdk';
import { useCapabilityTags, usePrefersReducedMotion } from './useCapabilityTags';
import { KpiCard, type KpiDensity } from './KpiCard';
import { KpiDetailPopover } from './KpiDetailPopover';

export const KPI_PINS_CHANGED_EVENT = 'empowernow:kpi-pins-changed';
export const STATS_CATALOG_ROUTE = '/operations-manager/stats';
const DENSITY_KEY = 'empowernow:kpi-density';

const POLL_MS = 30_000;

function useVisible(): boolean {
  const [visible, setVisible] = React.useState<boolean>(
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  );
  React.useEffect(() => {
    const onChange = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

function useDensity(): [KpiDensity, (d: KpiDensity) => void] {
  const [density, setDensityState] = React.useState<KpiDensity>(() => {
    try {
      return localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable';
    } catch {
      return 'comfortable';
    }
  });
  const setDensity = React.useCallback((d: KpiDensity) => {
    setDensityState(d);
    try {
      localStorage.setItem(DENSITY_KEY, d);
    } catch {
      /* non-fatal */
    }
  }, []);
  return [density, setDensity];
}

export default function YourStatsStrip() {
  const navigate = useNavigate();
  const reducedMotion = usePrefersReducedMotion();
  const { tags, loading: capLoading } = useCapabilityTags();
  const visible = useVisible();
  const [density, setDensity] = useDensity();

  const [catalog, setCatalog] = React.useState<KpiCatalog | null>(null);
  const [pins, setPins] = React.useState<string[]>([]);
  const [series, setSeries] = React.useState<KpiSeries>({});
  const [loading, setLoading] = React.useState(true);
  const [detailId, setDetailId] = React.useState<string | null>(null);
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [overId, setOverId] = React.useState<string | null>(null);

  // Load catalog + pins once.
  const loadBase = React.useCallback(async (signal?: AbortSignal) => {
    const [cat, pinIds] = await Promise.all([fetchCatalog(signal), fetchPins(signal)]);
    if (signal?.aborted) return;
    setCatalog(cat.catalog);
    setPins(pinIds);
    setLoading(false);
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    void loadBase(controller.signal);
    return () => controller.abort();
  }, [loadBase]);

  // React to pin changes from the catalog tab (same SPA session).
  React.useEffect(() => {
    const onPinsChanged = (e: Event) => {
      const next = (e as CustomEvent<{ pins?: string[] }>).detail?.pins;
      if (Array.isArray(next)) setPins(next.filter((x) => typeof x === 'string'));
      else void fetchPins().then(setPins);
    };
    window.addEventListener(KPI_PINS_CHANGED_EVENT, onPinsChanged as EventListener);
    return () => window.removeEventListener(KPI_PINS_CHANGED_EVENT, onPinsChanged as EventListener);
  }, []);

  // Resolve pinned metas (respect pin order; drop unknown/uncapable).
  const byId = React.useMemo(() => {
    const m = new Map<string, KpiMeta>();
    const allowed = filterByCapability(catalog?.kpis ?? [], tags);
    for (const k of allowed) m.set(k.id, k);
    return m;
  }, [catalog, tags]);

  const pinnedMetas = React.useMemo(
    () => pins.map((id) => byId.get(id)).filter((x): x is KpiMeta => !!x),
    [pins, byId],
  );

  const pinnedIds = React.useMemo(() => pinnedMetas.map((m) => m.id), [pinnedMetas]);
  const pinnedIdsKey = pinnedIds.join(',');

  // Poll snapshot values for the pinned ids while the tab is visible.
  React.useEffect(() => {
    if (pinnedIds.length === 0) {
      setSeries({});
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const tick = async () => {
      const s = await fetchSnapshot(pinnedIds, { signal: controller.signal });
      if (!cancelled) setSeries(s);
    };
    void tick();
    if (!visible) return () => { cancelled = true; controller.abort(); };
    const interval = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedIdsKey, visible]);

  const commitPins = React.useCallback((next: string[]) => {
    setPins(next); // optimistic
    void savePins(next).then((res) => {
      const applied = res.pins ?? next;
      setPins(applied);
      window.dispatchEvent(new CustomEvent(KPI_PINS_CHANGED_EVENT, { detail: { pins: applied } }));
    });
  }, []);

  const handleUnpin = React.useCallback(
    (id: string) => {
      commitPins(pins.filter((p) => p !== id));
      setDetailId((cur) => (cur === id ? null : cur));
    },
    [pins, commitPins],
  );

  const handleReorder = React.useCallback(
    (drag: string, target: string) => {
      const next = reorderByIds(pins, drag, target, true);
      if (next !== pins) commitPins(next);
    },
    [pins, commitPins],
  );

  const detailMeta = detailId ? byId.get(detailId) ?? null : null;

  // Authorization gate. The entire "Your stats" surface — the live strip AND the
  // "Browse catalog" discovery CTA — is a protected control: it renders only for
  // a subject that holds at least one metrics-view capability the catalog
  // requires. Fail closed — no tags, a degraded/failed capability projection, or
  // a catalog that has not loaded all resolve to "not authorized".
  const authorized = React.useMemo(() => {
    const required = new Set(
      (catalog?.kpis ?? []).map((k) => k.requiredCapability).filter(Boolean),
    );
    return tags.some((t) => required.has(t));
  }, [catalog, tags]);

  // Still resolving the catalog or the capability projection: reveal nothing.
  // A protected control must not flash before its gate is known.
  if (loading || capLoading) return null;

  // No metrics-view capability → hide the whole surface (strip + Browse CTA).
  if (!authorized) return null;

  // Empty (no pins): slim discovery CTA, only when a catalog is actually
  // reachable with KPIs the user can see (otherwise render nothing).
  if (pinnedMetas.length === 0) {
    const hasVisibleCatalog = byId.size > 0;
    if (!hasVisibleCatalog) return null;
    return (
      <section aria-label="Your stats" data-testid="home-stats-section" style={{ marginBottom: 16 }}>
        <div
          data-testid="home-stats-empty"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '12px 16px',
            background: 'var(--card-bg)',
            border: '1px dashed var(--card-border, var(--input-border))',
            borderRadius: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <GaugeGlyph />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--main-text)' }}>
                Pin the metrics that matter to you
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--main-text-secondary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Build a live health strip from {byId.size} operational KPIs across the platform.
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="home-stats-browse"
            onClick={() => navigate(STATS_CATALOG_ROUTE)}
            style={ctaButtonStyle}
          >
            Browse catalog
          </button>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Your stats" data-testid="home-stats-section" style={{ marginBottom: 16 }}>
      <StripHeading count={pinnedMetas.length} density={density} onDensity={setDensity} onManage={() => navigate(STATS_CATALOG_ROUTE)} />
      <div style={gridStyle} data-testid="home-stats-grid">
        {pinnedMetas.map((meta, i) => (
          <KpiCard
            key={meta.id}
            meta={meta}
            points={series[meta.id]}
            reducedMotion={reducedMotion}
            density={density}
            enterDelayMs={Math.min(i * 40, 320)}
            onOpenDetail={setDetailId}
            onUnpin={handleUnpin}
            draggable={pinnedMetas.length > 1}
            dragging={dragId === meta.id}
            dropBefore={!!dragId && overId === meta.id && dragId !== meta.id}
            onDragStart={(e) => {
              setDragId(meta.id);
              try {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', meta.id);
              } catch {
                /* jsdom */
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              try { e.dataTransfer.dropEffect = 'move'; } catch { /* jsdom */ }
              if (overId !== meta.id) setOverId(meta.id);
            }}
            onDragLeave={() => setOverId((cur) => (cur === meta.id ? null : cur))}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId && dragId !== meta.id) handleReorder(dragId, meta.id);
              setDragId(null);
              setOverId(null);
            }}
            onDragEnd={() => {
              setDragId(null);
              setOverId(null);
            }}
          />
        ))}
      </div>

      {detailMeta && (
        <KpiDetailPopover
          meta={detailMeta}
          points={series[detailMeta.id]}
          pinned={pins.includes(detailMeta.id)}
          reducedMotion={reducedMotion}
          onClose={() => setDetailId(null)}
          onTogglePin={handleUnpin}
        />
      )}
    </section>
  );
}

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
  gap: 12,
};

const ctaButtonStyle: React.CSSProperties = {
  flexShrink: 0,
  padding: '7px 14px',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--button-primary-text, #fff)',
  background: 'var(--button-primary-bg, var(--quantum-violet, var(--main-accent)))',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
};

function StripHeading({
  count,
  density,
  onDensity,
  onManage,
}: {
  count: number;
  density: KpiDensity;
  onDensity: (d: KpiDensity) => void;
  onManage: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <h2
        style={{
          margin: 0,
          fontSize: 16,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'var(--section-title-color, var(--main-text))',
        }}
      >
        Your stats
      </h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {count > 0 && <DensityToggle density={density} onDensity={onDensity} />}
        <button
          type="button"
          data-testid="home-stats-manage"
          onClick={onManage}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 10px',
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--main-accent)',
            background: 'transparent',
            border: '1px solid color-mix(in srgb, var(--main-accent) 30%, transparent)',
            borderRadius: 8,
            cursor: 'pointer',
          }}
          aria-label={`Manage pinned stats (${count} pinned)`}
        >
          Manage
        </button>
      </div>
    </div>
  );
}

function DensityToggle({ density, onDensity }: { density: KpiDensity; onDensity: (d: KpiDensity) => void }) {
  const btn = (mode: KpiDensity, label: string, glyph: React.ReactNode) => {
    const active = density === mode;
    return (
      <button
        type="button"
        data-testid={`home-stats-density-${mode}`}
        aria-pressed={active}
        aria-label={label}
        title={label}
        onClick={() => onDensity(mode)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 26,
          padding: 0,
          color: active ? 'var(--main-accent)' : 'var(--main-text-secondary)',
          background: active ? 'color-mix(in srgb, var(--main-accent) 14%, transparent)' : 'transparent',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
        }}
      >
        {glyph}
      </button>
    );
  };
  return (
    <div
      role="group"
      aria-label="Card density"
      style={{
        display: 'inline-flex',
        gap: 2,
        padding: 2,
        border: '1px solid var(--card-border, var(--input-border))',
        borderRadius: 8,
        background: 'var(--card-bg)',
      }}
    >
      {btn('comfortable', 'Comfortable cards', <ComfortableGlyph />)}
      {btn('compact', 'Compact cards', <CompactGlyph />)}
    </div>
  );
}

function ComfortableGlyph() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
      <rect x="4" y="4" width="16" height="7" rx="1.5" />
      <rect x="4" y="13" width="16" height="7" rx="1.5" />
    </svg>
  );
}

function CompactGlyph() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

function GaugeGlyph() {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--main-accent)"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d="M12 14 15 11" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </svg>
  );
}
